import type { DoctorShift } from '../../types/doctorShift';
import type {
  DoctorShiftRepository,
  CreateDoctorShiftParams,
  OperationResult,
} from './ports/DoctorShiftRepository';
import { validateShift, isPastDate } from '../../domain/doctorShift/ShiftValidationPolicy';

/**
 * 医師シフト作成ユースケース
 *
 * @description
 * 新規医師シフトを作成する。
 * Domain層のバリデーションポリシーを適用し、
 * 過去日付への作成は禁止する。
 *
 * @param {DoctorShiftRepository} repository - リポジトリ実装
 * @param {CreateDoctorShiftParams} params - 作成パラメータ
 * @returns {Promise<OperationResult<DoctorShift>>} 操作結果
 *
 * @example
 * const result = await createDoctorShiftUseCase(repository, {
 *   doctor_id: '123',
 *   shift_date: '2025-01-15',
 *   start_time: '09:00',
 *   end_time: '12:00'
 * });
 */
export const createDoctorShiftUseCase = async (
  repository: DoctorShiftRepository,
  params: CreateDoctorShiftParams
): Promise<OperationResult<DoctorShift>> => {
  // Domain層バリデーション
  const validation = validateShift({
    doctor_id: params.doctor_id,
    shift_date: params.shift_date,
    start_time: params.start_time,
    end_time: params.end_time,
  });

  if (!validation.isValid) {
    return {
      success: false,
      error: validation.errors.join('\n'),
      errorCode: 'VALIDATION_ERROR',
    };
  }

  // 過去日付チェック
  if (isPastDate(params.shift_date)) {
    return {
      success: false,
      error: '過去の日付にはシフトを作成できません。',
      errorCode: 'VALIDATION_ERROR',
    };
  }

  return repository.createShift(params);
};
